import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Zap, Target, TrendingUp, Database, BarChart3, Shield, Phone, MessageSquare, CheckCircle, ArrowRight, Cpu, Globe, Bot, Rocket, Users, Building2, Mail, Filter, Clock, Star, ChevronRight, Activity, Layers, RefreshCw, MapPin, Hash, CircleDot, ArrowUpRight } from 'lucide-react';

function LeadsCEE() {
  const [activeOption, setActiveOption] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    site_web: '',
    message: '',
    source: 'cee-leads-b2b'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({ leads: 0, taux: 0, roi: 0 });
  const [liveLeads, setLiveLeads] = useState([]);
  const [liveCount, setLiveCount] = useState(0);
  const liveRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const currentDate = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    axios.post(`${API_URL}/api/link-clicks/leads-cee`, {
      language: navigator.language,
      platform: navigator.userAgentData?.platform || navigator.platform,
      screen_width: window.screen.width,
      screen_height: window.screen.height
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setAnimatedStats({
        leads: Math.round(ease * 2500),
        taux: Math.round(ease * 34),
        roi: Math.round(ease * 720)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, []);

  // Live leads generation mockup
  const mockCompanies = [
    { name: 'Durand Industries', ville: 'Lyon', siret: '412 345 678', score: 92, secteur: 'Industrie', cee: 'BAT-TH-116' },
    { name: 'Logistique Express SAS', ville: 'Marseille', siret: '523 456 789', score: 87, secteur: 'Logistique', cee: 'BAT-EN-101' },
    { name: 'Hotel Le Meridien', ville: 'Paris', siret: '634 567 890', score: 95, secteur: 'Hotellerie', cee: 'BAT-TH-104' },
    { name: 'Boucherie Martin SARL', ville: 'Bordeaux', siret: '745 678 901', score: 78, secteur: 'Commerce', cee: 'BAT-EQ-133' },
    { name: 'Clinique Pasteur', ville: 'Toulouse', siret: '856 789 012', score: 91, secteur: 'Sante', cee: 'BAT-TH-113' },
    { name: 'Ferme Bio Dupuis', ville: 'Nantes', siret: '967 890 123', score: 84, secteur: 'Agriculture', cee: 'AGRI-TH-104' },
    { name: 'BTP Constructions Pro', ville: 'Lille', siret: '178 901 234', score: 89, secteur: 'BTP', cee: 'BAT-EN-108' },
    { name: 'Carrefour Market', ville: 'Nice', siret: '289 012 345', score: 76, secteur: 'Commerce', cee: 'BAT-EQ-127' },
    { name: 'DataCenter Omega', ville: 'Strasbourg', siret: '390 123 456', score: 98, secteur: 'Tertiaire', cee: 'BAT-TH-125' },
    { name: 'Transport Legrand', ville: 'Rennes', siret: '401 234 567', score: 82, secteur: 'Logistique', cee: 'BAT-EN-103' },
    { name: 'Residence Les Pins', ville: 'Montpellier', siret: '512 345 670', score: 93, secteur: 'Hotellerie', cee: 'BAT-TH-148' },
    { name: 'Usine Metalpro', ville: 'Grenoble', siret: '623 456 781', score: 88, secteur: 'Industrie', cee: 'IND-UT-117' },
  ];

  useEffect(() => {
    let idx = 0;
    const addLead = () => {
      const lead = { ...mockCompanies[idx % mockCompanies.length], id: Date.now(), isNew: true };
      setLiveLeads(prev => {
        const updated = [lead, ...prev].slice(0, 6);
        return updated;
      });
      setLiveCount(prev => prev + 1);
      idx++;
      // Remove "isNew" after animation
      setTimeout(() => {
        setLiveLeads(prev => prev.map(l => l.id === lead.id ? { ...l, isNew: false } : l));
      }, 600);
    };
    // Add first 3 immediately with small delays
    setTimeout(() => addLead(), 500);
    setTimeout(() => addLead(), 1200);
    setTimeout(() => addLead(), 2000);
    const interval = setInterval(addLead, 3500);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const submissionData = {
      ...formData,
      source: activeOption === 'demo' ? 'cee-demo-gratuite' : 'cee-abonnement-189',
      message: activeOption === 'demo'
        ? `[DEMO GRATUITE CEE B2B] ${formData.message || ''}`
        : `[ABONNEMENT 189EUR/mois CEE B2B] ${formData.message || ''}`
    };

    try {
      await axios.post(`${API_URL}/api/leads/public`, submissionData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Veuillez reessayer.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToOptions = () => {
    const el = document.getElementById('cee-options');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const selectOption = (option) => {
    setActiveOption(option);
    setTimeout(() => {
      const el = document.getElementById('cee-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const features = [
    { icon: Database, title: 'Detection Automatique', desc: 'Identification des entreprises eligibles CEE depuis les bases officielles B2B' },
    { icon: Filter, title: 'Filtrage Avance', desc: 'Qualification par secteur, taille, zone geographique et potentiel CEE' },
    { icon: Target, title: 'Scoring de Qualification', desc: 'Classement des prospects par probabilite de conversion sur 12 criteres' },
    { icon: TrendingUp, title: 'Suivi du Pipeline', desc: 'Visualisez chaque etape du parcours prospect en temps reel' },
    { icon: BarChart3, title: 'Dashboard Temps Reel', desc: 'Suivi des KPIs, taux de conversion et performance commerciale' },
    { icon: Shield, title: 'Conformite RGPD', desc: 'Donnees traitees en France, opt-out automatique, registre conforme' }
  ];

  const pipeline = [
    { step: '01', label: 'Ciblage', desc: 'La plateforme identifie les entreprises eligibles CEE', icon: Cpu },
    { step: '02', label: 'Enrichissement', desc: 'Donnees completees automatiquement (SIRET, contact, decisionnaire)', icon: Database },
    { step: '03', label: 'Scoring', desc: 'Algorithme de scoring sur 12 criteres de qualification', icon: Activity },
    { step: '04', label: 'Prospection', desc: 'Prise de contact ciblee et personnalisee', icon: Users },
    { step: '05', label: 'Conversion', desc: 'Leads qualifies livres prets a signer', icon: CheckCircle }
  ];

  const secteurs = [
    'Industrie', 'Tertiaire', 'Commerce', 'Logistique', 'Hotellerie', 'Sante', 'Agriculture', 'BTP'
  ];

  if (success) {
    return (
      <div className="cee-page">
        <style>{getStyles()}</style>
        <div className="cee-bg-gradient"></div>
        <div className="cee-bg-grid"></div>
        <div className="cee-bg-particles">
          {[...Array(20)].map((_, i) => <div key={i} className={`cee-particle p${i}`}></div>)}
        </div>
        <div className="cee-success-container">
          <div className="cee-success-card">
            <div className="cee-success-icon"><CheckCircle size={40} /></div>
            <h1>{activeOption === 'demo' ? 'Demo planifiee !' : 'Demande recue !'}</h1>
            <p>
              {activeOption === 'demo'
                ? 'Notre equipe vous contactera sous 24h pour organiser votre demo gratuite personnalisee.'
                : 'Votre demande d\'abonnement a bien ete enregistree. Un conseiller vous contactera pour finaliser l\'activation.'}
            </p>
            <div className="cee-success-info">
              <strong>Prochaine etape</strong>
              <span>{activeOption === 'demo'
                ? 'Presentation live de l\'outil avec vos criteres de ciblage CEE.'
                : 'Activation de votre compte sous 48h avec configuration personnalisee.'}</span>
            </div>
            <a href="/" className="cee-back-btn">Retour a l'accueil</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cee-page">
      <style>{getStyles()}</style>

      <div className="cee-bg-gradient"></div>
      <div className="cee-bg-grid"></div>
      <div className="cee-bg-scanlines"></div>
      <div className="cee-bg-particles">
        {[...Array(20)].map((_, i) => <div key={i} className={`cee-particle p${i}`}></div>)}
      </div>

      {/* Header */}
      <header className="cee-header">
        <div className="cee-logo">
          <div className="cee-logo-icon"><Zap size={20} /></div>
          <span className="cee-logo-text">CEE Leads<span className="cee-logo-pro">PRO</span></span>
        </div>
        <div className="cee-header-right">
          <span className="cee-date">{currentDate}</span>
          <button className="cee-header-cta" onClick={scrollToOptions}>
            Commencer <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="cee-hero">
        <div className="cee-hero-content">
          <div className="cee-hero-badge">
            <span className="cee-badge-pulse"></span>
            <Zap size={14} />
            <span>Plateforme B2B</span>
          </div>

          <h1 className="cee-hero-title">
            <span className="cee-title-line">Generez des</span>
            <span className="cee-title-gradient">Leads CEE B2B</span>
            <span className="cee-title-line">en pilote automatique</span>
          </h1>

          <p className="cee-hero-subtitle">
            Arretez de payer vos leads CEE a l'unite. Pour seulement 189EUR/mois,
            accedez a votre propre plateforme de generation de leads B2B en illimite.
            Generez autant de leads que vous voulez, sans aucune limite.
          </p>

          {/* Animated Stats */}
          <div className="cee-hero-stats">
            <div className="cee-stat-card">
              <div className="cee-stat-value">{animatedStats.leads.toLocaleString()}+</div>
              <div className="cee-stat-label">Leads generes/mois</div>
              <div className="cee-stat-bar"><div className="cee-stat-fill" style={{width:'85%'}}></div></div>
            </div>
            <div className="cee-stat-card">
              <div className="cee-stat-value">{animatedStats.taux}%</div>
              <div className="cee-stat-label">Taux de qualification</div>
              <div className="cee-stat-bar"><div className="cee-stat-fill" style={{width:'34%'}}></div></div>
            </div>
            <div className="cee-stat-card">
              <div className="cee-stat-value">{animatedStats.roi}%</div>
              <div className="cee-stat-label">ROI moyen constate</div>
              <div className="cee-stat-bar"><div className="cee-stat-fill" style={{width:'72%'}}></div></div>
            </div>
          </div>

          <div className="cee-hero-actions">
            <button className="cee-btn-primary" onClick={scrollToOptions}>
              <Rocket size={18} /> Decouvrir l'outil
            </button>
            <button className="cee-btn-ghost" onClick={() => selectOption('demo')}>
              Demander une demo <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Hero Visual - Live Platform Mockup */}
        <div className="cee-hero-visual">
          <div className="cee-platform">
            <div className="cee-platform-bar">
              <div className="cee-terminal-dots">
                <span className="cee-dot red"></span>
                <span className="cee-dot yellow"></span>
                <span className="cee-dot green"></span>
              </div>
              <span className="cee-platform-title">CEE Leads PRO - Dashboard</span>
              <div className="cee-platform-live">
                <CircleDot size={10} className="cee-live-dot" />
                <span>LIVE</span>
              </div>
            </div>

            {/* Mini stats bar */}
            <div className="cee-platform-stats-bar">
              <div className="cee-mini-stat">
                <span className="cee-mini-label">Leads aujourd'hui</span>
                <span className="cee-mini-value">{247 + liveCount}</span>
              </div>
              <div className="cee-mini-stat">
                <span className="cee-mini-label">Secteurs actifs</span>
                <span className="cee-mini-value">8</span>
              </div>
              <div className="cee-mini-stat">
                <span className="cee-mini-label">Qualification</span>
                <span className="cee-mini-value cee-mini-green">34%</span>
              </div>
            </div>

            {/* Live leads table */}
            <div className="cee-platform-table">
              <div className="cee-table-header">
                <span className="cee-th" style={{flex: 2}}>Entreprise</span>
                <span className="cee-th" style={{flex: 1}}>Secteur</span>
                <span className="cee-th" style={{flex: 1}}>Fiche CEE</span>
                <span className="cee-th cee-th-score">Score</span>
              </div>
              <div className="cee-table-body" ref={liveRef}>
                {liveLeads.map((lead) => (
                  <div key={lead.id} className={`cee-table-row ${lead.isNew ? 'cee-row-new' : ''}`}>
                    <div className="cee-td" style={{flex: 2}}>
                      <div className="cee-td-company">{lead.name}</div>
                      <div className="cee-td-meta"><MapPin size={10} /> {lead.ville} <span className="cee-td-siret"><Hash size={9} /> {lead.siret}</span></div>
                    </div>
                    <div className="cee-td cee-td-secteur" style={{flex: 1}}>
                      <span className="cee-secteur-tag">{lead.secteur}</span>
                    </div>
                    <div className="cee-td" style={{flex: 1}}>
                      <span className="cee-cee-code">{lead.cee}</span>
                    </div>
                    <div className="cee-td cee-th-score">
                      <div className={`cee-score-badge ${lead.score >= 90 ? 'hot' : lead.score >= 80 ? 'warm' : 'mid'}`}>
                        {lead.score}
                      </div>
                    </div>
                  </div>
                ))}
                {liveLeads.length === 0 && (
                  <div className="cee-table-loading">
                    <RefreshCw size={16} className="cee-spin" />
                    <span>Recherche en cours...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom bar */}
            <div className="cee-platform-bottom">
              <div className="cee-scanning-indicator">
                <RefreshCw size={12} className="cee-spin" />
                <span>Scan en cours... {247 + liveCount} leads generes</span>
              </div>
              <div className="cee-platform-badge-pro">PRO</div>
            </div>
          </div>
          <div className="cee-terminal-glow"></div>
        </div>
      </section>

      {/* Options Section */}
      <section className="cee-section" id="cee-options">
        <div className="cee-section-header">
          <h2 className="cee-section-title">Choisissez votre <span className="cee-gradient-text">option</span></h2>
          <p className="cee-section-desc">Testez gratuitement ou demarrez immediatement</p>
        </div>

        <div className="cee-options-grid">
          {/* Option 1 - Demo */}
          <div
            className={`cee-option-card ${activeOption === 'demo' ? 'active' : ''}`}
            onClick={() => selectOption('demo')}
          >
            <div className="cee-option-badge">Gratuit</div>
            <div className="cee-option-icon"><Rocket size={32} /></div>
            <h3>Demo Gratuite</h3>
            <p className="cee-option-desc">
              Decouvrez la puissance de notre outil avec une demonstration personnalisee en live.
            </p>
            <ul className="cee-option-features">
              <li><CheckCircle size={16} /> Presentation live 30 min</li>
              <li><CheckCircle size={16} /> Simulation sur vos criteres</li>
              <li><CheckCircle size={16} /> Rapport d'eligibilite offert</li>
              <li><CheckCircle size={16} /> Sans engagement</li>
            </ul>
            <div className="cee-option-price">
              <span className="cee-price-value">0 EUR</span>
              <span className="cee-price-period">Sans carte bancaire</span>
            </div>
            <button className="cee-option-btn demo" onClick={(e) => { e.stopPropagation(); selectOption('demo'); }}>
              Planifier ma demo <ArrowRight size={16} />
            </button>
          </div>

          {/* Option 2 - Abonnement */}
          <div
            className={`cee-option-card premium ${activeOption === 'subscribe' ? 'active' : ''}`}
            onClick={() => selectOption('subscribe')}
          >
            <div className="cee-option-badge hot">Populaire</div>
            <div className="cee-option-icon"><Zap size={32} /></div>
            <h3>Abonnement Pro</h3>
            <p className="cee-option-desc">
              Acces complet a la plateforme de generation de leads CEE B2B avec support prioritaire.
            </p>
            <ul className="cee-option-features">
              <li><CheckCircle size={16} /> Leads illimites</li>
              <li><CheckCircle size={16} /> Scoring predictif avance</li>
              <li><CheckCircle size={16} /> Prospection automatisee</li>
              <li><CheckCircle size={16} /> Dashboard + export</li>
              <li><CheckCircle size={16} /> Support prioritaire</li>
              <li><CheckCircle size={16} /> Mises a jour incluses</li>
            </ul>
            <div className="cee-option-price">
              <span className="cee-price-value">189 EUR</span>
              <span className="cee-price-period">/ mois HT</span>
            </div>
            <button className="cee-option-btn subscribe" onClick={(e) => { e.stopPropagation(); selectOption('subscribe'); }}>
              Souscrire maintenant <Zap size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Form Section */}
      {activeOption && (
        <section className="cee-section" id="cee-form">
          <div className="cee-form-wrapper">
            <div className="cee-form-card">
              <div className="cee-form-header">
                <h2>
                  {activeOption === 'demo' ? 'Planifier ma demo gratuite' : 'Souscrire a l\'abonnement Pro'}
                </h2>
                <p>
                  {activeOption === 'demo'
                    ? 'Remplissez le formulaire et notre equipe vous contactera sous 24h.'
                    : 'Remplissez vos informations pour demarrer votre abonnement a 189 EUR/mois.'}
                </p>
              </div>

              {error && <div className="cee-error-msg">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="cee-input-row">
                  <div className="cee-input-group">
                    <label>Nom complet <span className="cee-required">*</span></label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      onFocus={() => setFocusedField('nom')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="Jean Dupont"
                      className={focusedField === 'nom' ? 'focused' : ''}
                    />
                  </div>
                  <div className="cee-input-group">
                    <label>Email professionnel <span className="cee-required">*</span></label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="jean@entreprise.fr"
                      className={focusedField === 'email' ? 'focused' : ''}
                    />
                  </div>
                </div>

                <div className="cee-input-row">
                  <div className="cee-input-group">
                    <label>Telephone <span className="cee-required">*</span></label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      onFocus={() => setFocusedField('telephone')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="06 12 34 56 78"
                      className={focusedField === 'telephone' ? 'focused' : ''}
                    />
                  </div>
                  <div className="cee-input-group">
                    <label>Entreprise <span className="cee-required">*</span></label>
                    <input
                      type="text"
                      value={formData.entreprise}
                      onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                      onFocus={() => setFocusedField('entreprise')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="Votre societe"
                      className={focusedField === 'entreprise' ? 'focused' : ''}
                    />
                  </div>
                </div>

                <div className="cee-input-group">
                  <label>Site web</label>
                  <input
                    type="text"
                    value={formData.site_web}
                    onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                    onFocus={() => setFocusedField('site_web')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="www.votresite.fr"
                    className={focusedField === 'site_web' ? 'focused' : ''}
                  />
                </div>

                <div className="cee-input-group">
                  <label>Precisions sur votre besoin</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    placeholder={activeOption === 'demo'
                      ? 'Secteurs cibles, volume souhaite, zone geographique...'
                      : 'Vos objectifs, secteurs cibles, volume mensuel attendu...'}
                    rows={3}
                    className={focusedField === 'message' ? 'focused' : ''}
                  />
                </div>

                <button type="submit" className={`cee-submit-btn ${activeOption}`} disabled={loading}>
                  {loading ? (
                    <><RefreshCw size={16} className="cee-spin" /> Envoi en cours...</>
                  ) : activeOption === 'demo' ? (
                    <>Planifier ma demo gratuite <ArrowRight size={16} /></>
                  ) : (
                    <>Souscrire a 189 EUR/mois <Zap size={16} /></>
                  )}
                </button>

                <p className="cee-disclaimer">
                  {activeOption === 'demo'
                    ? 'Demo sans engagement. Vos donnees sont protegees et ne seront pas partagees.'
                    : 'Abonnement sans engagement de duree. Resiliable a tout moment. Vos donnees sont protegees.'}
                </p>

                <div className="cee-contact-buttons">
                  <a href="https://wa.me/33743678501?text=Bonjour%2C%20je%20suis%20interesse%20par%20l%27outil%20CEE%20Leads%20B2B." target="_blank" rel="noopener noreferrer" className="cee-whatsapp-btn">
                    <MessageSquare size={14} /> WhatsApp
                  </a>
                  <a href="tel:+33743678501" className="cee-phone-btn">
                    <Phone size={14} /> 07 43 67 85 01
                  </a>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="cee-footer">
        <div className="cee-footer-content">
          <div className="cee-footer-brand">
            <Zap size={18} />
            <span>CEE Leads PRO</span>
          </div>
          <p>Plateforme de generation de leads CEE B2B</p>
          <p className="cee-footer-copy">E-Shops Connect - {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}

function getStyles() {
  return `
    @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Inter:wght@400;500;600;700;800;900&display=swap');

    .cee-page {
      min-height: 100vh;
      background: #050510;
      font-family: 'Inter', -apple-system, sans-serif;
      position: relative;
      overflow-x: hidden;
      color: #fff;
    }

    .cee-bg-gradient {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background:
        radial-gradient(ellipse at 15% 15%, rgba(0,255,136,0.08) 0%, transparent 50%),
        radial-gradient(ellipse at 85% 20%, rgba(0,200,255,0.06) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 80%, rgba(120,0,255,0.05) 0%, transparent 50%);
      pointer-events: none; z-index: 0;
    }

    .cee-bg-grid {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background-image:
        linear-gradient(rgba(0,255,136,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,255,136,0.03) 1px, transparent 1px);
      background-size: 60px 60px;
      pointer-events: none; z-index: 0;
    }

    .cee-bg-scanlines {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,136,0.008) 2px, rgba(0,255,136,0.008) 4px);
      pointer-events: none; z-index: 0;
    }

    .cee-bg-particles {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none; z-index: 1; overflow: hidden;
    }

    .cee-particle {
      position: absolute;
      width: 2px; height: 2px;
      background: rgba(0,255,136,0.6);
      border-radius: 50%;
      animation: cee-float-particle 15s linear infinite;
    }

    ${[...Array(20)].map((_, i) => `
      .cee-particle.p${i} {
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
        animation-delay: ${Math.random() * 15}s;
        animation-duration: ${10 + Math.random() * 20}s;
        opacity: ${0.2 + Math.random() * 0.5};
        width: ${1 + Math.random() * 3}px;
        height: ${1 + Math.random() * 3}px;
      }
    `).join('')}

    @keyframes cee-float-particle {
      0% { transform: translateY(100vh) translateX(0); opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { transform: translateY(-100px) translateX(${Math.random() > 0.5 ? '' : '-'}80px); opacity: 0; }
    }

    /* Header */
    .cee-header {
      position: relative; z-index: 10;
      padding: 20px 40px;
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 1px solid rgba(0,255,136,0.08);
      backdrop-filter: blur(10px);
    }

    .cee-logo {
      display: flex; align-items: center; gap: 10px;
    }

    .cee-logo-icon {
      width: 40px; height: 40px;
      background: linear-gradient(135deg, #00ff88 0%, #00ccff 100%);
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      color: #050510;
      box-shadow: 0 0 20px rgba(0,255,136,0.3);
    }

    .cee-logo-text {
      font-size: 20px; font-weight: 800; color: #fff;
    }

    .cee-logo-pro {
      font-size: 11px; font-weight: 700;
      background: linear-gradient(135deg, #00ff88, #00ccff);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      margin-left: 4px; vertical-align: super;
    }

    .cee-header-right {
      display: flex; align-items: center; gap: 20px;
    }

    .cee-date {
      color: rgba(255,255,255,0.3); font-size: 13px; text-transform: capitalize;
    }

    .cee-header-cta {
      display: flex; align-items: center; gap: 6px;
      padding: 10px 20px;
      background: rgba(0,255,136,0.1); border: 1px solid rgba(0,255,136,0.3);
      border-radius: 8px; color: #00ff88; font-size: 13px; font-weight: 600;
      cursor: pointer; transition: all 0.3s;
    }

    .cee-header-cta:hover {
      background: rgba(0,255,136,0.2); transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(0,255,136,0.2);
    }

    /* Hero */
    .cee-hero {
      position: relative; z-index: 10;
      display: flex; max-width: 1300px; margin: 0 auto;
      padding: 60px 40px 80px; gap: 60px; align-items: center;
    }

    .cee-hero-content { flex: 1 1 55%; }
    .cee-hero-visual { flex: 1 1 45%; max-width: 520px; }

    .cee-hero-badge {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 16px; border-radius: 50px;
      background: rgba(0,255,136,0.06); border: 1px solid rgba(0,255,136,0.2);
      color: #00ff88; font-size: 12px; font-weight: 600;
      text-transform: uppercase; letter-spacing: 1px; margin-bottom: 25px;
    }

    .cee-badge-pulse {
      width: 8px; height: 8px; background: #00ff88; border-radius: 50%;
      animation: cee-pulse 2s infinite;
    }

    @keyframes cee-pulse {
      0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(0,255,136,0.4); }
      50% { opacity: 0.8; box-shadow: 0 0 0 8px rgba(0,255,136,0); }
    }

    .cee-hero-title {
      font-size: 54px; font-weight: 900; line-height: 1.1;
      margin-bottom: 25px; letter-spacing: -2px;
    }

    .cee-title-line { display: block; color: #fff; }

    .cee-title-gradient {
      display: block;
      background: linear-gradient(135deg, #00ff88 0%, #00ccff 50%, #7c3aed 100%);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      animation: cee-gradient-shift 4s ease-in-out infinite;
      background-size: 200% 200%;
    }

    @keyframes cee-gradient-shift {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }

    .cee-hero-subtitle {
      font-size: 17px; line-height: 1.8; color: rgba(255,255,255,0.55);
      margin-bottom: 35px; max-width: 520px;
    }

    /* Stats Cards */
    .cee-hero-stats {
      display: flex; gap: 16px; margin-bottom: 35px;
    }

    .cee-stat-card {
      flex: 1; padding: 18px;
      background: rgba(0,255,136,0.03); border: 1px solid rgba(0,255,136,0.1);
      border-radius: 12px; transition: all 0.3s;
    }

    .cee-stat-card:hover {
      border-color: rgba(0,255,136,0.3);
      background: rgba(0,255,136,0.06);
      transform: translateY(-2px);
    }

    .cee-stat-value {
      font-size: 28px; font-weight: 900;
      font-family: 'JetBrains Mono', monospace;
      background: linear-gradient(135deg, #00ff88, #00ccff);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }

    .cee-stat-label {
      font-size: 11px; color: rgba(255,255,255,0.4);
      margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px;
    }

    .cee-stat-bar {
      margin-top: 10px; height: 3px; background: rgba(255,255,255,0.06);
      border-radius: 3px; overflow: hidden;
    }

    .cee-stat-fill {
      height: 100%; border-radius: 3px;
      background: linear-gradient(90deg, #00ff88, #00ccff);
      animation: cee-bar-fill 2s ease-out forwards;
      transform-origin: left;
    }

    @keyframes cee-bar-fill {
      from { transform: scaleX(0); }
      to { transform: scaleX(1); }
    }

    /* Hero Actions */
    .cee-hero-actions {
      display: flex; gap: 15px; align-items: center;
    }

    .cee-btn-primary {
      display: flex; align-items: center; gap: 8px;
      padding: 16px 28px;
      background: linear-gradient(135deg, #00ff88 0%, #00ccaa 100%);
      border: none; border-radius: 12px;
      font-size: 15px; font-weight: 700; color: #050510;
      cursor: pointer; transition: all 0.3s;
      box-shadow: 0 4px 20px rgba(0,255,136,0.3);
    }

    .cee-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,255,136,0.4);
    }

    .cee-btn-ghost {
      display: flex; align-items: center; gap: 6px;
      padding: 16px 24px;
      background: transparent; border: 1px solid rgba(255,255,255,0.15);
      border-radius: 12px;
      font-size: 15px; font-weight: 600; color: #fff;
      cursor: pointer; transition: all 0.3s;
    }

    .cee-btn-ghost:hover {
      border-color: rgba(0,255,136,0.4);
      background: rgba(0,255,136,0.05);
    }

    /* Platform Mockup */
    .cee-hero-visual {
      position: relative;
    }

    .cee-platform {
      background: rgba(10,10,30,0.95);
      border: 1px solid rgba(0,255,136,0.15);
      border-radius: 12px; overflow: hidden;
      box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,255,136,0.05);
    }

    .cee-platform-bar {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 16px;
      background: rgba(255,255,255,0.03);
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .cee-terminal-dots {
      display: flex; gap: 6px;
    }

    .cee-dot {
      width: 10px; height: 10px; border-radius: 50%;
    }

    .cee-dot.red { background: #ff5f57; }
    .cee-dot.yellow { background: #ffbd2e; }
    .cee-dot.green { background: #28c840; }

    .cee-platform-title {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px; color: rgba(255,255,255,0.4); flex: 1;
    }

    .cee-platform-live {
      display: flex; align-items: center; gap: 5px;
      font-size: 10px; font-weight: 700; color: #00ff88;
      text-transform: uppercase; letter-spacing: 1px;
    }

    .cee-live-dot {
      color: #00ff88;
      animation: cee-pulse 1.5s infinite;
    }

    /* Mini stats bar */
    .cee-platform-stats-bar {
      display: flex; gap: 0;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .cee-mini-stat {
      flex: 1; padding: 10px 14px;
      border-right: 1px solid rgba(255,255,255,0.04);
      display: flex; flex-direction: column; gap: 2px;
    }

    .cee-mini-stat:last-child { border-right: none; }

    .cee-mini-label {
      font-size: 9px; color: rgba(255,255,255,0.35);
      text-transform: uppercase; letter-spacing: 0.5px;
    }

    .cee-mini-value {
      font-family: 'JetBrains Mono', monospace;
      font-size: 18px; font-weight: 700; color: #fff;
    }

    .cee-mini-unit {
      font-size: 11px; color: rgba(255,255,255,0.3); font-weight: 400;
    }

    .cee-mini-green { color: #00ff88; }

    /* Table */
    .cee-platform-table {
      max-height: 280px; overflow: hidden;
    }

    .cee-table-header {
      display: flex; padding: 8px 14px;
      background: rgba(0,255,136,0.03);
      border-bottom: 1px solid rgba(0,255,136,0.08);
    }

    .cee-th {
      font-size: 9px; font-weight: 700; color: rgba(0,255,136,0.6);
      text-transform: uppercase; letter-spacing: 0.8px;
    }

    .cee-th-score { width: 50px; text-align: center; flex: none !important; }

    .cee-table-body {
      position: relative;
    }

    .cee-table-row {
      display: flex; align-items: center;
      padding: 10px 14px;
      border-bottom: 1px solid rgba(255,255,255,0.03);
      transition: all 0.4s ease;
      background: transparent;
    }

    .cee-table-row:hover {
      background: rgba(0,255,136,0.03);
    }

    .cee-row-new {
      animation: cee-row-slide-in 0.5s ease-out;
      background: rgba(0,255,136,0.06);
    }

    @keyframes cee-row-slide-in {
      from { transform: translateY(-100%); opacity: 0; background: rgba(0,255,136,0.15); }
      to { transform: translateY(0); opacity: 1; background: rgba(0,255,136,0.06); }
    }

    .cee-td {
      font-size: 12px; color: rgba(255,255,255,0.7);
    }

    .cee-td-company {
      font-weight: 600; color: #fff; font-size: 12px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    .cee-td-meta {
      display: flex; align-items: center; gap: 4px;
      font-size: 10px; color: rgba(255,255,255,0.3); margin-top: 2px;
    }

    .cee-td-siret {
      display: inline-flex; align-items: center; gap: 2px;
      margin-left: 6px;
    }

    .cee-secteur-tag {
      font-size: 10px; padding: 2px 8px;
      background: rgba(0,204,255,0.08); border: 1px solid rgba(0,204,255,0.15);
      border-radius: 4px; color: #00ccff; white-space: nowrap;
    }

    .cee-cee-code {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px; color: rgba(255,255,255,0.5);
    }

    .cee-score-badge {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 8px; font-family: 'JetBrains Mono', monospace;
      font-size: 13px; font-weight: 700;
    }

    .cee-score-badge.hot {
      background: rgba(0,255,136,0.12); color: #00ff88;
      border: 1px solid rgba(0,255,136,0.3);
      box-shadow: 0 0 10px rgba(0,255,136,0.15);
    }

    .cee-score-badge.warm {
      background: rgba(0,204,255,0.1); color: #00ccff;
      border: 1px solid rgba(0,204,255,0.25);
    }

    .cee-score-badge.mid {
      background: rgba(255,189,46,0.1); color: #ffbd2e;
      border: 1px solid rgba(255,189,46,0.2);
    }

    .cee-table-loading {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 30px; color: rgba(255,255,255,0.3); font-size: 12px;
    }

    /* Bottom bar */
    .cee-platform-bottom {
      display: flex; align-items: center; justify-content: space-between;
      padding: 8px 14px;
      background: rgba(255,255,255,0.02);
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    .cee-scanning-indicator {
      display: flex; align-items: center; gap: 6px;
      font-size: 10px; color: rgba(0,255,136,0.5);
      font-family: 'JetBrains Mono', monospace;
    }

    .cee-platform-badge-pro {
      font-size: 9px; font-weight: 800; padding: 2px 8px;
      background: linear-gradient(135deg, #00ff88, #00ccff);
      color: #050510; border-radius: 4px; letter-spacing: 1px;
    }

    .cee-terminal-glow {
      position: absolute; top: 50%; left: 50%;
      width: 120%; height: 120%;
      transform: translate(-50%, -50%);
      background: radial-gradient(ellipse, rgba(0,255,136,0.06) 0%, transparent 60%);
      pointer-events: none; z-index: -1;
    }

    /* Sections */
    .cee-section {
      position: relative; z-index: 10;
      max-width: 1200px; margin: 0 auto;
      padding: 60px 40px;
    }

    .cee-section-header {
      text-align: center; margin-bottom: 50px;
    }

    .cee-section-title {
      font-size: 38px; font-weight: 800; color: #fff; margin-bottom: 12px;
    }

    .cee-gradient-text {
      background: linear-gradient(135deg, #00ff88, #00ccff);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }

    .cee-section-desc {
      font-size: 16px; color: rgba(255,255,255,0.5);
    }

    /* Pipeline */
    .cee-pipeline {
      display: flex; gap: 0; justify-content: center; position: relative;
    }

    .cee-pipeline-step {
      flex: 1; text-align: center; padding: 30px 20px;
      position: relative;
      background: rgba(0,255,136,0.02); border: 1px solid rgba(0,255,136,0.08);
      border-radius: 16px; margin: 0 8px;
      transition: all 0.3s;
    }

    .cee-pipeline-step:hover {
      background: rgba(0,255,136,0.06);
      border-color: rgba(0,255,136,0.25);
      transform: translateY(-4px);
    }

    .cee-pipeline-number {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px; font-weight: 700; color: #00ff88;
      text-transform: uppercase; letter-spacing: 2px;
      margin-bottom: 12px;
    }

    .cee-pipeline-icon {
      color: #00ccff; margin-bottom: 12px;
    }

    .cee-pipeline-step h3 {
      font-size: 16px; font-weight: 700; margin-bottom: 8px;
    }

    .cee-pipeline-step p {
      font-size: 12px; color: rgba(255,255,255,0.45); line-height: 1.5;
    }

    .cee-pipeline-connector {
      display: none;
    }

    /* Features Grid */
    .cee-features-grid {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
    }

    .cee-feature-card {
      position: relative; overflow: hidden;
      padding: 30px;
      background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
      border-radius: 16px; transition: all 0.4s;
    }

    .cee-feature-card:hover {
      border-color: rgba(0,255,136,0.3);
      background: rgba(0,255,136,0.04);
      transform: translateY(-4px);
    }

    .cee-feature-glow {
      position: absolute; top: -50%; right: -50%;
      width: 100%; height: 100%;
      background: radial-gradient(circle, rgba(0,255,136,0.08) 0%, transparent 70%);
      opacity: 0; transition: opacity 0.4s;
    }

    .cee-feature-card:hover .cee-feature-glow {
      opacity: 1;
    }

    .cee-feature-icon {
      color: #00ff88; margin-bottom: 16px;
      width: 48px; height: 48px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,255,136,0.08); border-radius: 12px;
    }

    .cee-feature-card h3 {
      font-size: 17px; font-weight: 700; margin-bottom: 8px;
    }

    .cee-feature-card p {
      font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.6;
    }

    /* Sectors */
    .cee-sectors {
      display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;
    }

    .cee-sector-pill {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 20px;
      background: rgba(0,255,136,0.04); border: 1px solid rgba(0,255,136,0.12);
      border-radius: 50px; font-size: 14px; color: rgba(255,255,255,0.8);
      transition: all 0.3s;
    }

    .cee-sector-pill:hover {
      background: rgba(0,255,136,0.1);
      border-color: rgba(0,255,136,0.3);
      color: #00ff88;
      transform: translateY(-2px);
    }

    .cee-sector-pill svg { color: #00ff88; }

    /* Options */
    .cee-options-grid {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: 30px;
      max-width: 900px; margin: 0 auto;
    }

    .cee-option-card {
      position: relative; padding: 40px;
      background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.08);
      border-radius: 20px; cursor: pointer; transition: all 0.4s;
    }

    .cee-option-card:hover, .cee-option-card.active {
      border-color: rgba(0,255,136,0.4);
      background: rgba(0,255,136,0.04);
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 30px rgba(0,255,136,0.08);
    }

    .cee-option-card.premium {
      border-color: rgba(0,255,136,0.15);
      background: rgba(0,255,136,0.03);
    }

    .cee-option-card.premium:hover, .cee-option-card.premium.active {
      border-color: rgba(0,255,136,0.5);
      box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 40px rgba(0,255,136,0.12);
    }

    .cee-option-badge {
      position: absolute; top: 20px; right: 20px;
      padding: 5px 12px; border-radius: 50px;
      font-size: 11px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.5px;
      background: rgba(0,255,136,0.1); color: #00ff88;
      border: 1px solid rgba(0,255,136,0.2);
    }

    .cee-option-badge.hot {
      background: linear-gradient(135deg, rgba(0,255,136,0.2), rgba(0,204,255,0.2));
      border-color: rgba(0,255,136,0.4);
    }

    .cee-option-icon {
      color: #00ff88; margin-bottom: 20px;
      width: 60px; height: 60px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,255,136,0.08); border-radius: 16px;
    }

    .cee-option-card h3 {
      font-size: 24px; font-weight: 800; margin-bottom: 10px;
    }

    .cee-option-desc {
      font-size: 14px; color: rgba(255,255,255,0.5); line-height: 1.6;
      margin-bottom: 20px;
    }

    .cee-option-features {
      list-style: none; padding: 0; margin: 0 0 25px 0;
    }

    .cee-option-features li {
      display: flex; align-items: center; gap: 10px;
      padding: 8px 0; font-size: 14px; color: rgba(255,255,255,0.75);
    }

    .cee-option-features li svg { color: #00ff88; flex-shrink: 0; }

    .cee-option-price {
      margin-bottom: 20px; padding: 15px 0;
      border-top: 1px solid rgba(255,255,255,0.06);
    }

    .cee-price-value {
      font-size: 36px; font-weight: 900;
      font-family: 'JetBrains Mono', monospace;
      background: linear-gradient(135deg, #00ff88, #00ccff);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }

    .cee-price-period {
      display: block; font-size: 13px; color: rgba(255,255,255,0.4); margin-top: 4px;
    }

    .cee-option-btn {
      width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 15px; border: none; border-radius: 12px;
      font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.3s;
    }

    .cee-option-btn.demo {
      background: rgba(0,255,136,0.1); color: #00ff88;
      border: 1px solid rgba(0,255,136,0.3);
    }

    .cee-option-btn.demo:hover {
      background: rgba(0,255,136,0.2);
      box-shadow: 0 4px 20px rgba(0,255,136,0.2);
    }

    .cee-option-btn.subscribe {
      background: linear-gradient(135deg, #00ff88, #00ccaa);
      color: #050510;
      box-shadow: 0 4px 20px rgba(0,255,136,0.3);
    }

    .cee-option-btn.subscribe:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,255,136,0.4);
    }

    /* Form */
    .cee-form-wrapper {
      max-width: 650px; margin: 0 auto;
    }

    .cee-form-card {
      background: rgba(255,255,255,0.02);
      backdrop-filter: blur(20px);
      border-radius: 24px; border: 1px solid rgba(0,255,136,0.12);
      padding: 40px;
      box-shadow: 0 25px 50px rgba(0,0,0,0.3), 0 0 30px rgba(0,255,136,0.05);
    }

    .cee-form-header {
      text-align: center; margin-bottom: 30px;
    }

    .cee-form-header h2 {
      font-size: 26px; font-weight: 800; margin-bottom: 8px;
    }

    .cee-form-header p {
      font-size: 14px; color: rgba(255,255,255,0.5);
    }

    .cee-input-group {
      margin-bottom: 18px; flex: 1;
    }

    .cee-input-row {
      display: flex; gap: 15px;
    }

    .cee-input-group label {
      display: block; font-size: 12px; font-weight: 600;
      color: rgba(255,255,255,0.6); margin-bottom: 8px;
      text-transform: uppercase; letter-spacing: 0.5px;
    }

    .cee-required { color: #00ff88; }

    .cee-input-group input,
    .cee-input-group textarea {
      width: 100%; padding: 14px 16px;
      background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
      border-radius: 10px; font-size: 14px; color: #fff;
      outline: none; transition: all 0.3s; box-sizing: border-box;
      font-family: inherit;
    }

    .cee-input-group input::placeholder,
    .cee-input-group textarea::placeholder {
      color: rgba(255,255,255,0.25);
    }

    .cee-input-group input.focused,
    .cee-input-group textarea.focused {
      border-color: #00ff88;
      background: rgba(0,255,136,0.03);
      box-shadow: 0 0 0 3px rgba(0,255,136,0.08);
    }

    .cee-input-group textarea {
      resize: vertical; min-height: 80px;
    }

    .cee-submit-btn {
      width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 16px; border: none; border-radius: 12px;
      font-size: 16px; font-weight: 700; cursor: pointer;
      transition: all 0.3s; margin-top: 5px;
    }

    .cee-submit-btn.demo {
      background: linear-gradient(135deg, #00ccff, #7c3aed);
      color: #fff;
      box-shadow: 0 4px 20px rgba(0,204,255,0.3);
    }

    .cee-submit-btn.subscribe {
      background: linear-gradient(135deg, #00ff88, #00ccaa);
      color: #050510;
      box-shadow: 0 4px 20px rgba(0,255,136,0.3);
    }

    .cee-submit-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,255,136,0.4);
    }

    .cee-submit-btn:disabled {
      opacity: 0.6; cursor: not-allowed;
    }

    .cee-spin {
      animation: cee-spinner 1s linear infinite;
    }

    @keyframes cee-spinner {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .cee-disclaimer {
      text-align: center; font-size: 11px;
      color: rgba(255,255,255,0.3); margin-top: 15px; line-height: 1.5;
    }

    .cee-contact-buttons {
      display: flex; gap: 10px; justify-content: center; margin-top: 20px;
    }

    .cee-whatsapp-btn, .cee-phone-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 10px 18px; border-radius: 10px;
      font-size: 13px; font-weight: 600; text-decoration: none;
      transition: all 0.3s;
    }

    .cee-whatsapp-btn {
      background: linear-gradient(135deg, #25D366, #128C7E);
      color: white; box-shadow: 0 4px 15px rgba(37,211,102,0.3);
    }

    .cee-whatsapp-btn:hover { transform: translateY(-2px); }

    .cee-phone-btn {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.15); color: white;
    }

    .cee-phone-btn:hover { background: rgba(255,255,255,0.1); transform: translateY(-2px); }

    .cee-error-msg {
      background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3);
      color: #ef4444; padding: 12px 15px; border-radius: 10px;
      margin-bottom: 18px; font-size: 13px;
    }

    /* Success */
    .cee-success-container {
      min-height: 100vh; display: flex; align-items: center;
      justify-content: center; padding: 40px; position: relative; z-index: 10;
    }

    .cee-success-card {
      background: rgba(255,255,255,0.03); backdrop-filter: blur(20px);
      border-radius: 24px; border: 1px solid rgba(0,255,136,0.15);
      padding: 50px; text-align: center; max-width: 450px;
    }

    .cee-success-icon {
      width: 80px; height: 80px;
      background: linear-gradient(135deg, #00ff88, #00ccaa);
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      margin: 0 auto 25px; color: #050510;
      box-shadow: 0 10px 30px rgba(0,255,136,0.3);
    }

    .cee-success-card h1 { font-size: 28px; margin-bottom: 15px; }
    .cee-success-card p { color: rgba(255,255,255,0.6); line-height: 1.7; margin-bottom: 25px; }

    .cee-success-info {
      background: rgba(0,255,136,0.06); border: 1px solid rgba(0,255,136,0.15);
      border-radius: 12px; padding: 20px; margin-bottom: 25px; text-align: left;
    }

    .cee-success-info strong { display: block; color: #00ff88; margin-bottom: 8px; }
    .cee-success-info span { color: rgba(255,255,255,0.6); font-size: 14px; }

    .cee-back-btn {
      display: inline-block; padding: 14px 28px;
      background: rgba(0,255,136,0.1); border: 1px solid rgba(0,255,136,0.3);
      border-radius: 10px; color: #00ff88; text-decoration: none;
      font-weight: 600; transition: all 0.3s;
    }

    .cee-back-btn:hover { background: rgba(0,255,136,0.2); }

    /* Footer */
    .cee-footer {
      position: relative; z-index: 10;
      border-top: 1px solid rgba(0,255,136,0.08);
      padding: 40px;
    }

    .cee-footer-content {
      max-width: 1200px; margin: 0 auto; text-align: center;
    }

    .cee-footer-brand {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      font-size: 18px; font-weight: 700; color: #00ff88; margin-bottom: 10px;
    }

    .cee-footer p { font-size: 13px; color: rgba(255,255,255,0.3); }
    .cee-footer-copy { margin-top: 10px; font-size: 12px; }

    /* Responsive */
    @media (max-width: 1024px) {
      .cee-hero {
        flex-direction: column; padding: 40px 20px 60px;
      }
      .cee-hero-visual { max-width: 100%; }
      .cee-hero-title { font-size: 42px; }
      .cee-hero-stats { flex-wrap: wrap; }
      .cee-stat-card { flex: 1 1 calc(50% - 8px); }
      .cee-pipeline { flex-wrap: wrap; }
      .cee-pipeline-step { flex: 1 1 calc(50% - 16px); min-width: 200px; }
      .cee-features-grid { grid-template-columns: repeat(2, 1fr); }
      .cee-options-grid { grid-template-columns: 1fr; max-width: 450px; }
    }

    @media (max-width: 768px) {
      .cee-header { padding: 15px 20px; }
      .cee-date { display: none; }
      .cee-hero-title { font-size: 34px; letter-spacing: -1px; }
      .cee-hero-subtitle { font-size: 15px; }
      .cee-hero-stats { flex-direction: column; }
      .cee-stat-card { flex: 1 1 100%; }
      .cee-hero-actions { flex-direction: column; }
      .cee-btn-primary, .cee-btn-ghost { width: 100%; justify-content: center; }
      .cee-pipeline-step { flex: 1 1 100%; }
      .cee-features-grid { grid-template-columns: 1fr; }
      .cee-section { padding: 40px 20px; }
      .cee-section-title { font-size: 28px; }
      .cee-form-card { padding: 25px 20px; }
      .cee-input-row { flex-direction: column; gap: 0; }
      .cee-contact-buttons { flex-direction: column; }
      .cee-whatsapp-btn, .cee-phone-btn { justify-content: center; }
      .cee-option-card { padding: 25px; }
    }

    @media (max-width: 480px) {
      .cee-hero-title { font-size: 28px; }
      .cee-terminal-body { font-size: 11px; padding: 12px; }
    }
  `;
}

export default LeadsCEE;
